/**
 * VOCABULARY REGISTRY — i valori che i plugin aggiungono ai vocabolari del
 * prodotto.
 *
 * ## Il problema
 *
 * Stati del preventivo e tipi di allegato sono estendibili: il backend lo
 * prevede da sempre (`QuotationStatusContributor`, `QuotationFileTypeContributor`)
 * e i valori viaggiano come interi, con una fascia da mille riservata a ciascun
 * plugin. Il frontend però conosceva solo i valori del core: un intero fuori
 * elenco cadeva sul valore di ripiego del vocabolario, e un allegato DXF
 * diventava "allegato generico" — etichetta sbagliata a video, e il plugin
 * incapace di riconoscere i propri file.
 *
 * ## La forma della soluzione
 *
 * Il plugin dichiara le proprie voci nel manifest, il core le fonde con le
 * proprie al boot. Nessuna chiamata di rete e nessuno stato da invalidare:
 * spegnendo il plugin le sue voci spariscono da sole, esattamente come le sue
 * rotte e i suoi campi.
 *
 * ## Perché non un endpoint (decisione confermata in code review)
 *
 * Perché il vocabolario non cambia a runtime: cambia quando cambia l'insieme
 * dei plugin attivi, che è la stessa cosa che fa cambiare rotte, menu e
 * contributor. Scaricarlo introdurrebbe una fase asincrona nel boot — con
 * tutto quello che comporta per il primo render — per un dato che il bundle
 * già contiene.
 *
 * L'argomento è diventato più forte con l'uso: il registro viene consultato
 * **dentro la conversione dei DTO** (`quotationStatusFromDto`,
 * `quotationFileTypeFromDto`) e nella risoluzione delle etichette. Sono
 * funzioni sincrone chiamate a ogni risposta REST: un vocabolario caricato via
 * rete costringerebbe ognuna a gestire lo stato "non ancora pronto".
 *
 * ## Il prezzo, dichiarato
 *
 * La stessa voce è dichiarata **due volte**: qui, via `vocabularyEntries` del
 * manifest, e lato server dal `QuotationFileTypeContributor` del plugin. Le due
 * devono riportare valore, chiave e chiave i18n identici, e **niente lo
 * verifica**. Se divergono, il valore ripiega sul default del vocabolario, che è
 * un'etichetta plausibile: il difetto non si vede.
 *
 * È il prezzo scelto, non un difetto residuo. Chi tocca una delle due metà tocca
 * l'altra nello stesso commit. Il modo per rendere il vincolo verificabile
 * invece che affidato alla diligenza è un controllo di build che confronti i
 * manifest con quanto emettono i contributor: non c'è, perché nel monorepo non
 * c'è ancora infrastruttura di test, e sarebbe la prima.
 *
 * Nota sull'asimmetria fra le due metà: i contributor lato server sono gated per
 * company (`isEnabled(groupId)`), questo registro no — `loadPlugins` registra le
 * voci di ogni plugin presente nel bundle. Il registro è quindi un
 * sovrainsieme di ciò che il backend accetta. Innocuo in lettura, da non usare
 * per decidere cosa OFFRIRE all'utente.
 *
 * Read-only dopo il bootstrap, come `entityRegistry` e `registry` dei plugin.
 */

import type { ExtensibleVocabulary, VocabularyEntry } from '@crm/contracts';

interface VocabularyMaps {
  keyByValue: Map<number, string>;
  valueByKey: Map<string, number>;
  labelKeyByKey: Map<string, string>;
}

const vocabularies = new Map<ExtensibleVocabulary, VocabularyMaps>();

function mapsFor(vocabulary: ExtensibleVocabulary): VocabularyMaps {
  let maps = vocabularies.get(vocabulary);
  if (!maps) {
    maps = { keyByValue: new Map(), valueByKey: new Map(), labelKeyByKey: new Map() };
    vocabularies.set(vocabulary, maps);
  }
  return maps;
}

/**
 * Registra le voci contribuite da un plugin.
 *
 * Una collisione — due plugin che dichiarano lo stesso valore numerico nello
 * stesso vocabolario — **lancia**. È lo stesso trattamento che
 * `registerEntityType` riserva ai typeName duplicati, e per la stessa ragione:
 * il valore finisce in colonna, quindi due significati sullo stesso numero
 * sono dati corrotti che nessun log fa notare. Meglio non partire.
 */
export function registerVocabularyEntries(entries: VocabularyEntry[]): void {
  for (const entry of entries) {
    const maps = mapsFor(entry.vocabulary);

    const existingKey = maps.keyByValue.get(entry.value);
    if (existingKey !== undefined && existingKey !== entry.key)
      throw new Error(
        `[VocabularyRegistry] Il valore ${entry.value} del vocabolario ` +
        `"${entry.vocabulary}" è già assegnato a "${existingKey}", non può ` +
        `valere anche "${entry.key}". Le fasce sono in ` +
        'CONVENTIONS_LIFERAY_MODULES.md §4.12.1.',
      );

    maps.keyByValue.set(entry.value, entry.key);
    maps.valueByKey.set(entry.key, entry.value);
    maps.labelKeyByKey.set(entry.key, entry.labelKey);
  }
}

/** La chiave contribuita per quel valore, `undefined` se nessun plugin la dichiara. */
export function vocabularyKeyFromValue(
  vocabulary: ExtensibleVocabulary,
  value: number,
): string | undefined {
  return vocabularies.get(vocabulary)?.keyByValue.get(value);
}

/** Il valore contribuito per quella chiave, `undefined` se non è di un plugin. */
export function vocabularyValueFromKey(
  vocabulary: ExtensibleVocabulary,
  key: string,
): number | undefined {
  return vocabularies.get(vocabulary)?.valueByKey.get(key);
}

/**
 * Il namespace i18n del prodotto per ciascun vocabolario.
 *
 * Sta qui e non nei singoli componenti perché è la seconda metà di
 * `vocabularyLabelKey`: chi mostra un valore non deve sapere se è del prodotto
 * o di un plugin, e senza questa tabella dovrebbe deciderlo lui — che è
 * esattamente il modo in cui l'etichetta si rompeva prima, con la chiave
 * costruita concatenando il namespace core a un valore che core non era.
 *
 * ATTENZIONE per chi cerca chiavi i18n inutilizzate: le chiavi sotto questi due
 * namespace sono composte a runtime e **non compaiono come stringhe letterali**
 * nel sorgente. `quotation.fileTypes.quotation` e `quotation.fileTypes.attachment`
 * risultano non riferite a una ricerca testuale, e non lo sono: le costruisce la
 * funzione qui sotto. Cancellarle toglierebbe l'etichetta ai documenti.
 */
const CORE_NAMESPACE: Record<ExtensibleVocabulary, string> = {
  quotationStatus: 'quotation.statuses',
  quotationFileType: 'quotation.fileTypes',
};

/**
 * La chiave i18n con cui etichettare un valore del vocabolario, qualunque sia
 * la sua provenienza: la dichiara il plugin se il valore è contribuito, la
 * ricava dal namespace del prodotto altrimenti.
 *
 * È l'unico modo giusto di ottenere l'etichetta di uno stato o di un tipo di
 * allegato — la lista, il chip, la riga documento. Restituisce sempre una
 * chiave: per un valore che nessuno dichiara restituisce quella del prodotto,
 * che i18next rende come la chiave stessa. Preferibile a una stringa vuota,
 * perché a video si vede che manca una traduzione invece di un buco.
 */
export function vocabularyLabelKey(
  vocabulary: ExtensibleVocabulary,
  key: string,
): string {
  const contributed = vocabularies.get(vocabulary)?.labelKeyByKey.get(key);

  return contributed ?? `${CORE_NAMESPACE[vocabulary]}.${key}`;
}
